import * as React from 'react';

// Material-UI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import { DialogContent } from '@mui/material';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import SendIcon from '@mui/icons-material/Send';
import TextField from '@mui/material/TextField';
import { useSnackbar } from 'notistack';

// Styles
import { useTheme } from '@mui/material/styles';

import FeedbackTypeSelect from './FeedbackTypeSelect';
import RichTextEditor from '../../shared/RichTextEditor';

import {
  API_URL,
  NULL_ID
} from '../../global';

import {
  HTTPHeaders
} from '../../interfaces';

import {
  FeedbackFormData,
  FeedbackType
} from '../../models';

// Auth0 user hooks 
import { useAuth0User } from '../../hooks/UseAuth0User';

export interface FeedbackFormProps {
  open: boolean;
  onClose: () => void;
}

const FeedbackForm: React.FC<FeedbackFormProps> = (props) => {

  // Hooks
  const { enqueueSnackbar } = useSnackbar();
  const { getAccessToken, isAuthenticated, memberID } = useAuth0User();

  // Local state
  const [feedback, setFeedback] = React.useState<FeedbackFormData>(new FeedbackFormData());
  const [feedbackTypes, setFeedbackTypes] = React.useState<Array<FeedbackType>>([]);
  const [formDisabled, setFormDisabled] = React.useState<boolean>(true);
  const [showWorking, setShowWorking] = React.useState<boolean>(false);

  // Ensures the editor only sets the starting text once.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialContent = React.useMemo(() => feedback.description, []);

  // #region Styles
  const theme = useTheme();
  const styles = {
    button: {
      margin: theme.spacing(0, 4),
    },
    title: {
      flexGrow: 1,
      margin: theme.spacing(2, 0, 3, 0)
    },
    typeContainer: {
      margin: theme.spacing(1, 0, 1, 0)
    },
    editorWrapper: {
      width: '100%',
      marginTop: theme.spacing(1),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      overflow: 'hidden' // Keeps the editor corners clean
    },
  };

  // #endregion

  // #region Methods

  /**
   * Fetches the list of available feedback types from the administrative API.
   * This lookup data is typically used to populate dropdowns or selection lists.
   */
  const fetchFeedbackType = async () => {
    // 1. Retrieve the Auth0 Access Token for Bearer authentication.
    const accessToken = await getAccessToken();

    // 2. Prepare the standard security and content-type headers.
    const headers: HTTPHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    };

    // 3. Execute the GET request to the administrative feedback types endpoint.
    await fetch(`${API_URL}/v1/admin/feedback/types`, {
      method: 'GET',
      cache: 'no-cache', // Ensure we get fresh data rather than a stale browser-cached response
      headers: headers,
      mode: 'cors'
    })
      .then((response) => {
        // 4. Verify the response status (checks for 2xx range).
        if (!response.ok) throw new Error('Feedback types not retrieved');
        return response.json();
      })
      .then((ft: Array<FeedbackType>) => {
        // 5. Update the local state with the returned array of feedback categories.
        setFeedbackTypes(ft);
      })
      .catch(() => {
        // 6. Alert the user via a snackbar if the lookup data fails to load.
        enqueueSnackbar('Feedback types not retrieved', { variant: 'error' });
      });
  }

  /**
   * Resets the feedback form to its default values.
   * This clears all user input while ensuring the current member's identity 
   * is preserved for the next submission.
   */
  const resetFeedback = () => {
    // 1. Instantiate a fresh data object with default model values.
    const f = new FeedbackFormData();

    // 2. Update the feedback state.
    // We use the spread operator to apply defaults, then immediately 
    // re-assign the 'userID' so the form remains linked to the active member.
    setFeedback({
      ...f,
      'userID': memberID()
    });
  };

  /**
   * Submits the feedback data to the backend administrative API.
   * This function manages UI locking during the request and handles
   * cleanup/navigation upon successful submission.
   */
  const sendFeedback = async () => {
    // 1. Enter a 'busy' state: Disable the form to prevent double-submissions
    // and show a progress spinner to the user.
    setFormDisabled(true);
    setShowWorking(true);

    // 2. Authorize the request with an Auth0 JWT token.
    const accessToken = await getAccessToken();
    const headers: HTTPHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    };

    const url = new URL(`${API_URL}/v1/admin/feedback`);

    // 3. Dispatch the POST request with the feedback state object.
    await fetch(url.toString(), {
      method: 'POST',
      body: JSON.stringify(feedback),
      headers: headers,
      mode: 'cors'
    })
      .then((response) => {
        // 4. Handle HTTP failure status codes (e.g., 400 or 500 errors).
        if (!response.ok) {
          enqueueSnackbar(`WARNING: Feedback was not submitted`, { variant: 'warning' });
        } else {
          // 5. On success: Notify user, wipe form data, and close the dialog/panel.
          enqueueSnackbar(`Feedback was submitted`, { variant: 'success' });
          resetFeedback();
          props.onClose();
        }
        // 6. Return UI to interactive state.
        setShowWorking(false);
      })
      .catch(error => {
        // 7. Handle network-level errors (e.g., DNS failure, timeout).
        enqueueSnackbar(`WARNING: Feedback was not submitted, Error: ${error}`, { variant: 'error' });
        setShowWorking(false);
      })
      .finally(() => {
        setShowWorking(false);
      });
  }

  /**
   * Validates the rich text editor content by stripping HTML and checking for meaningful text.
   * This prevents users from submitting feedback that contains only whitespace or empty tags.
   */
  const validateContent = () => {
    // 1. Use a regular expression to identify and remove all HTML tags (e.g., <p>, <br>, </div>).
    // This extracts the "raw" human-readable text from the TinyMCE HTML string.
    const plainText = feedback.description.replace(/<[^>]*>/g, '').trim();

    // 2. Check the resulting string length. 
    // If the length is 0, it means the description was either empty or contained only HTML markup.
    if (plainText.length === 0) {
      return false; // Validation failed: No actual text found.
    }

    // 3. Validation passed: The user has provided descriptive text.
    return true;
  };

  /**
   * Evaluates the feedback form's validity to toggle the state of the submission controls.
   * This checks that a category is selected, a title exists, and the description 
   * contains actual text content.
   */
  const validateFeedback = () => {
    // We enable the form only if all three mandatory criteria are satisfied:
    if (
      // 1. A specific feedback type/category has been selected (ID is not the default null ID)
      (feedback.feedbackType.feedbackTypeID !== NULL_ID) &&

      // 2. The title field is not null, undefined, or an empty string
      feedback.title &&

      // 3. The rich text content passes the HTML-stripping text check
      validateContent()
    ) {
      // All conditions met: Allow the user to interact with the 'Send' button
      setFormDisabled(false);
    } else {
      // Requirements not met: Lock the form/button to prevent incomplete submissions
      setFormDisabled(true);
    }
  };

  // #endregion

  // #region Event handlers

  const handleEditorChange = (content: string) => {
    setFeedback({ ...feedback, description: content });
  };

  const handleFeedbackTypeChange = (id: number) => {
    const feedbackType = feedbackTypes.find(ft => ft.feedbackTypeID === id);
    if (feedbackType !== undefined) {
      setFeedback({ ...feedback, feedbackType: feedbackType });
    }
  };

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.name === 'title') {
      setFeedback({ ...feedback, [event.target.name]: event.target.value });
    }
  };

  React.useEffect(() => {
    if (isAuthenticated && (memberID() !== NULL_ID)) {
      setFeedback(f => ({ ...f, 'userID': memberID() }));
    }
    fetchFeedbackType();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    validateFeedback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedback.description, feedback.title, feedback.feedbackType.feedbackTypeID]);

  // #endregion

  return (
    <Dialog fullWidth={true} maxWidth={"md"} open={props.open} onClose={props.onClose}>
      <DialogTitle>Website Feedback</DialogTitle>
      <DialogContent>
        <form autoComplete="off" noValidate>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }} sx={styles.typeContainer}>
              <FeedbackTypeSelect
                feedbackTypes={feedbackTypes}
                feedbackTypeID={feedback.feedbackType.feedbackTypeID ? feedback.feedbackType.feedbackTypeID : NULL_ID}
                feedbackTypeSelected={handleFeedbackTypeChange}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                name="title"
                value={feedback.title}
                required
                onChange={handleTitleChange}
                label="Summary of feedback"
                placeholder="Enter a short summary of the issue"
                sx={styles.title}
              />
            </Grid>
            <Grid size={12}>
              <Box style={styles.editorWrapper}>
                <RichTextEditor
                  initialValue={initialContent}
                  onChange={handleEditorChange}
                />
              </Box>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        {showWorking && <CircularProgress size={30} />}
        <Button onClick={props.onClose} color="primary">
          Close
        </Button>
        <Button
          variant="contained"
          sx={styles.button}
          onClick={sendFeedback}
          color="primary"
          endIcon={<SendIcon />}
          disabled={formDisabled}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default FeedbackForm;